import {
	BriefcaseBusiness,
	Check,
	ChevronDown,
	FileBadge,
	GraduationCap,
	Lightbulb,
	ThumbsUp,
	TriangleAlert,
} from "lucide-react";
import React, { useState } from "react";

import { Button } from "@/app/components/ui/button";
import { cn } from "@/app/handlers/cn.utils";

import { Badge } from "../../../components/Badge/Badge";
import { FitCheckAssessment } from "../config/api";

interface FitCheckReportProps {
	fitCheck: FitCheckAssessment;
}

interface BaselineItemProps {
	title: string;
	status: "MEET" | "PARTIAL" | "MISSING";
	note: string;
	icon: React.ReactNode;
}

const BaselineItem = ({ title, status, note, icon }: BaselineItemProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const getBgHex = (status: string | undefined) => {
		if (status === "MEET") return "#DCFCE7"; // tailwind green-200
		if (status === "PARTIAL" || status === "MISSING") return "#FEF9C2"; // tailwind yellow-200 (approx)
		return undefined;
	};

	const getSquareStyles = (status: string | undefined) => {
		if (status === "MEET") {
			return { bg: "#F0FDF4", textClass: "text-green-700" };
		}
		if (status === "PARTIAL" || status === "MISSING") {
			return { bg: "#FEFCE8", textClass: "text-yellow-700" };
		}
		return { bg: "#FFFFFF", textClass: "text-gray-700" };
	};

	const square = getSquareStyles(status);

	return (
		<div
			className={cn("rounded-lg border transition-all overflow-hidden", {
				"border-[#B9F8CF]": status === "MEET",
				"border-[#FFF085]": status === "PARTIAL" || status === "MISSING",
			})}
		>
			<div
				onClick={() => setIsOpen(!isOpen)}
				className={cn("w-full p-4 flex items-center justify-between text-left cursor-pointer")}
				style={{ backgroundColor: getBgHex(status) }}
			>
				<div className="flex items-center gap-3 h-full">
					<div
						className={cn("w-12 aspect-square flex items-center justify-center rounded", square.textClass)}
						style={{ backgroundColor: square.bg }}
					>
						{icon}
					</div>
					<div>
						<p className="font-semibold text-gray-900">{title}</p>
						<Badge
							variant={status === "MEET" ? "default" : "secondary"}
							className={
								status === "MEET"
									? "bg-green-100 text-green-700 hover:bg-green-100"
									: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
							}
						>
							{status === "MEET" ? "Match" : "Gap"}
						</Badge>
					</div>
				</div>
				<ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
			</div>
			{isOpen && (
				<div className="px-4 pb-4">
					<p className="text-sm text-gray-700">{note}</p>
				</div>
			)}
		</div>
	);
};

function FitCheckReport({ fitCheck }: FitCheckReportProps) {
	const baselineItems = [
		{ item: "Education", status: fitCheck.baselineCheck.education.status },
		{ item: "Experience", status: fitCheck.baselineCheck.experience.status },
		{ item: "Skills", status: fitCheck.baselineCheck.certification.status },
		{ item: "Certification", status: fitCheck.baselineCheck.certification.status },
	];

	return (
		<div className="max-w-4xl mx-auto p-6 space-y-8">
			{/* Baseline Check Section */}
			<section>
				<h2 className="text-2xl font-bold text-gray-900 mb-4">Baseline Check</h2>

				{/* Status Badges */}
				<div className="flex flex-wrap gap-2 mb-4">
					{baselineItems.map(({ item, status }) => (
						<Badge
							key={item}
							variant={status === "MEET" ? "default" : "secondary"}
							className={
								status === "MEET"
									? "bg-green-100 text-green-700 hover:bg-green-100"
									: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
							}
						>
							{status === "MEET" ? `Meets ${item}` : `Misses ${item}`}
						</Badge>
					))}
				</div>

				{/* Baseline Items */}
				<div className="space-y-3">
					<BaselineItem
						title="Education"
						status={fitCheck.baselineCheck.education.status}
						note={fitCheck.baselineCheck.education.note}
						icon={<GraduationCap className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Work Experience"
						status={fitCheck.baselineCheck.experience.status}
						note={fitCheck.baselineCheck.experience.note}
						icon={<BriefcaseBusiness className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Skills"
						status={fitCheck.baselineCheck.certification.status}
						note={fitCheck.baselineCheck.certification.note}
						icon={<Lightbulb className="w-6 h-6" />}
					/>
					<BaselineItem
						title="Certifications & Licenses"
						status={fitCheck.baselineCheck.certification.status}
						note={fitCheck.baselineCheck.certification.note}
						icon={<FileBadge className="w-6 h-6" />}
					/>
				</div>
			</section>

			<div className="">
				<div className="flex items-start gap-3 mb-2">
					<ThumbsUp className="w-6 h-6 text-green-700 flex-shrink-0 mt-0.5" />
					<div>
						<h2 className="text-xl font-bold text-gray-900">What You&apos;re Already Bringing</h2>
					</div>
				</div>
				<ul className="space-y-2">
					{fitCheck.strengths.map((strength, index) => (
						<li key={index} className="flex items-start gap-2 text-gray-700">
							<Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
							<span>{strength}</span>
						</li>
					))}
				</ul>
			</div>

			<div className="">
				<div className="flex items-start gap-3 mb-2">
					<TriangleAlert className="w-6 h-6 text-yellow-700 flex-shrink-0 mt-0.5" />
					<div>
						<h2 className="text-xl font-bold text-gray-900">Where This Job Will Challenge You</h2>
					</div>
				</div>
				<ul className="space-y-2">
					{fitCheck.gaps.map((gap, index) => (
						<li key={index} className="flex items-start gap-2 text-gray-700">
							<Check className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
							<span>{gap}</span>
						</li>
					))}
				</ul>
			</div>

			<div className="">
				<h2 className="text-xl font-bold text-gray-900 mb-2">Decision Guidance</h2>
				<p className="text-gray-700 mb-6">{fitCheck.encouragementNote}</p>

				<div className="grid md:grid-cols-2 gap-6">
					{/* Option 1: Apply Now */}
					<div className="bg-white rounded-lg p-6 border border-gray-200 space-y-3">
						<h3 className="text-lg font-bold text-gray-900 mb-3">Option 1: Apply Now</h3>
						<p className="text-sm text-gray-600 mb-4">
							You meet many requirements. Here&apos;s how to strengthen your application:
						</p>
						<ul className="space-y-3">
							{fitCheck.decisionGuidance.ifApplyNow.map((tip, index) => (
								<li key={index} className="flex items-start gap-2 text-sm text-gray-700">
									<Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-2" />
									<span>{tip}</span>
								</li>
							))}
						</ul>
						<Button className="w-full" variant="tonal">
							Apply Now
						</Button>
					</div>

					{/* Option 2: Close Gaps First */}
					<div className="bg-white rounded-lg p-6 border border-gray-200 space-y-3">
						<h3 className="text-lg font-bold text-gray-900 mb-3">Option 2: Close Gaps First</h3>
						<p className="text-sm text-gray-600 mb-4">Prefer to improve first? Here&apos;s what to focus on:</p>
						<ul className="space-y-3">
							{fitCheck.decisionGuidance.ifCloseGapsFirst.map((action, index) => (
								<li key={index} className="flex items-start gap-2 text-sm text-gray-700">
									<Check className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-2" />
									<span>{action}</span>
								</li>
							))}
						</ul>
						<Button variant="outline" className="w-full border-primary-700 text-primary-700">
							Close Gaps
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default FitCheckReport;
